import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AddressDTO, API } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Point } from 'geojson';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-comp-address-component',
  templateUrl: './address-component.component.html',
  styleUrls: ['./address-component.component.scss'],
  standalone: false,
})
export class AddressComponentComponent implements OnInit {
  _address: AddressDTO;

  @Input() hideCoordinates: boolean = false;
  _noValidation: boolean = false;
  @Input() set noValidation(val: boolean) {
    this._noValidation = val;
    if (val) {
      this.form.clearValidators();
      this.form.updateValueAndValidity();
    }
  }

  get noValidation(): boolean {
    return this._noValidation;
  }

  @Input() set address(val: AddressDTO) {
    this._address = val;
    if (val) {
      this.updateForm();
    } else {
      const address: AddressDTO = new AddressDTO();
      address.country = 'DE';
      this._address = address;
    }
    this.recreatePositionIfMissing();
    this.cdRef.detectChanges();
  }

  get address() {
    return this._address;
  }

  _point: any;
  @Input() set point(val: any) {
    this._point = val;
    this.positionFromMap(val);
  }

  get point() {
    return this._point;
  }

  @Output() addressEmitter: EventEmitter<AddressDTO>;
  @Output() pointEmitter: EventEmitter<any>;

  form: UntypedFormGroup;

  constructor(
    private api: API,
    public errorHandler: ErrorService,
    public util: UtilService,
    private translate: TranslateService,
    public modal: ModalService,
    private cdRef: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {
    this.addressEmitter = new EventEmitter<AddressDTO>();
    this.pointEmitter = new EventEmitter<any>();
    this.form = new UntypedFormGroup({
      AddressStreet: new UntypedFormControl('', [Validators.required]),
      AddressStreet2: new UntypedFormControl(''),
      AddressCity: new UntypedFormControl('', [Validators.required]),
      AddressZip: new UntypedFormControl('', [Validators.required]),
      Latitude: new UntypedFormControl(''),
      Longitude: new UntypedFormControl(''),
      AddressState: new UntypedFormControl(''),
      AddressBorough: new UntypedFormControl(''),
      AddressCounty: new UntypedFormControl(''),
    });
  }

  ngOnInit(): void {
    this.registerFormChangesListener();
  }

  get AddressStreet() {
    return this.form.get('AddressStreet');
  }

  get AddressStreet2() {
    return this.form.get('AddressStreet2');
  }

  get AddressCity() {
    return this.form.get('AddressCity');
  }

  get AddressZip() {
    return this.form.get('AddressZip');
  }

  get AddressState() {
    return this.form.get('AddressState');
  }

  get Latitude() {
    return this.form.get('Latitude');
  }

  get Longitude() {
    return this.form.get('Longitude');
  }

  get AddressBorough() {
    return this.form.get('AddressBorough');
  }

  get AddressCounty() {
    return this.form.get('AddressCounty');
  }

  registerFormChangesListener() {
    this.AddressStreet.valueChanges.subscribe(value => {
      this.address.street = value;
      this.addressEmitter.emit(this.address);
    });
    this.AddressStreet2.valueChanges.subscribe(value => {
      this.address.street2 = value;
      this.addressEmitter.emit(this.address);
    });
    this.AddressCity.valueChanges.subscribe(value => {
      this.address.city = value;
      this.addressEmitter.emit(this.address);
    });
    this.AddressZip.valueChanges.subscribe(value => {
      this.address.zip = value;
      this.addressEmitter.emit(this.address);
    });
    this.AddressState.valueChanges.subscribe(value => {
      this.address.state = value;
      this.addressEmitter.emit(this.address);
    });
    this.AddressCounty.valueChanges.subscribe(value => {
      this.address.county = value;
      this.addressEmitter.emit(this.address);
    });
    this.AddressBorough.valueChanges.subscribe(value => {
      this.address.borough = value;
      this.addressEmitter.emit(this.address);
    });
    this.Latitude.valueChanges.subscribe(value => {
      if (value) {
        value = value.toString().replace(',', '.');
      } else {
        value = null;
      }
      this.setSelectedLocationCoords();
      this.recreatePositionIfMissing();
      this.address.position.coordinates[1] = value;
      this.checkNoCoords();
      this.addressEmitter.emit(this.address);
    });
    this.Longitude.valueChanges.subscribe(value => {
      if (value) {
        value = value.toString().replace(',', '.');
      } else {
        value = null;
      }
      this.setSelectedLocationCoords();
      this.recreatePositionIfMissing();
      this.address.position.coordinates[0] = value;
      this.checkNoCoords();
      this.addressEmitter.emit(this.address);
    });
  }

  checkNoCoords() {
    if (this.address.position.coordinates[0] === null && this.address.position.coordinates[1] === null) {
      this.address.position = {
        type: 'Point',
        coordinates: [null, null],
      };
      this.cdRef.detectChanges();
    }
  }

  recreatePositionIfMissing() {
    if (!this.address?.position) {
      this.address.position = {
        type: 'Point',
        coordinates: [null, null],
      };
      this.cdRef.detectChanges();
      this.addressEmitter.emit(this._address);
    }
  }

  updateForm() {
    if (!this.address) {
      return;
    }
    this.AddressCity.setValue(this.address.city);
    if (this.address.city) {
      this.AddressCity.markAsTouched();
    }
    this.AddressState.setValue(this.address.state);
    if (this.address.state) {
      this.AddressState.markAsTouched();
    }
    this.AddressStreet.setValue(this.address.street);
    if (this.address.street) {
      this.AddressStreet.markAsTouched();
    }
    this.AddressStreet2.setValue(this.address.street2);
    if (this.address.street2) {
      this.AddressStreet2.markAsTouched();
    }
    this.AddressZip.setValue(this.address.zip);
    if (this.address.zip) {
      this.AddressZip.markAsTouched();
    }
    this.AddressBorough.setValue(this.address.borough);
    if (this.address.borough) {
      this.AddressBorough.markAsTouched();
    }
    this.AddressCounty.setValue(this.address.county);
    if (this.address.county) {
      this.AddressCounty.markAsTouched();
    }

    if (this.address.position?.coordinates) {
      this.Longitude.setValue(this.address.position.coordinates[0]);
      this.Longitude.markAsTouched();
      this.Latitude.setValue(this.address.position.coordinates[1]);
      this.Latitude.markAsTouched();
    }
  }

  setSelectedLocationCoords() {
    this._point = {
      x: this.Longitude.value?.toString().replace(',', '.') as number,
      y: this.Latitude.value?.toString().replace(',', '.') as number,
      zoom: 16,
    };
    this.pointEmitter.emit(this.point);
  }

  previewPosition() {
    this.api
      .dm_geo_Address_geocode(this.address)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Point) => {
          if (!data) {
            this.modal
              .alert(this.translate.instant('lang.geocode.addressNotFound'), this.translate.instant('lang.geocode.addressNotResolvable'))
              .then(() => {})
              .catch(() => {});
            return;
          }
          this.address.position = data;
          if (data?.coordinates) {
            this.Longitude.setValue(data.coordinates[0]);
            this.Latitude.setValue(data.coordinates[1]);
          } else {
            this.Longitude.setValue(null);
            this.Latitude.setValue(null);
          }
          this.setSelectedLocationCoords();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  positionFromMap(pos: { x: number; y: number }) {
    if (pos?.x) {
      this.Longitude.setValue(pos.x);
    }
    if (pos?.y) {
      this.Latitude.setValue(pos.y);
    }
  }

  countryUpdate($event: string) {
    this._address.country = $event;
    this.addressEmitter.emit(this.address);
  }
}
